import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';

import { useSDK, useUIState } from '../../../../app/App';
import { styled } from '../../../../styled';
import { isMobile } from '../../../../utils/isMobile';
import { CustomRenderThumbVertical } from '../../../customRenderThumbVertical';
import { CustomRenderThumbView } from '../../../customRenderThumbView';
import { DateDelimiterSticky } from '../dateDelimiter/dateDelimiterSticky';
import { useOnMarkRead } from '../hooks/useOnMarkRead';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { useOnScroll } from '../hooks/useScrollOnNewEvent';
import { ScrollToBottomButton } from '../scrollToBottomButton';

export const ScrollbarContainer = styled(
	observer(
		({
			className,
			listRef,
			children,
		}: {
			className: string;
			listRef: RefObject<HTMLDivElement>;
			children: ReactNode;
		}): ReactElement => {
			const [state] = useState({ prScrollHeight: 0 });
			const scrollbarRef = useRef<Scrollbars>(null);
			const scrollBarWrapperRef = useRef<HTMLDivElement>(null);
			const rootStore = useSDK();
			const { eventsStore } = rootStore;
			const { scrollStore, loadingStore } = useUIState();

			const onScroll = useOnScroll(scrollbarRef);

			useOnMarkRead(rootStore, scrollbarRef);
			useResizeObserver(listRef, scrollbarRef);

			useLayoutEffect(() => {
				if (scrollbarRef.current) {
					if (state.prScrollHeight) {
						scrollbarRef.current.scrollTop(scrollbarRef.current.getScrollHeight() - state.prScrollHeight);
						state.prScrollHeight = scrollbarRef.current.getScrollHeight() || 0;
					}
				}
			}, [eventsStore.dialogsRendered, state]);

			useLayoutEffect(() => {
				if (listRef.current) {
					scrollStore.setListWrapper(listRef.current);
				}
				if (scrollBarWrapperRef.current) {
					scrollStore.setScrollWrapper(scrollBarWrapperRef.current);
				}
				if (scrollbarRef.current) {
					scrollStore.setScrollBar(scrollbarRef.current);
				}
			}, [listRef, scrollStore]);

			// при повторном открытии виджет-чата восстанавлием высоту скрола
			useEffect(() => {
				if (scrollStore.scrollPosition === undefined) {
					scrollStore.delayedScroll(scrollStore.scrollToBottom, 400);
				} else if (scrollStore.restoreScroll()) {
					scrollStore.delayedScroll(
						() => scrollStore.scrollBar?.scrollTop(scrollStore.lastScrollPosition ?? 0),
						400
					);
				}
				// если скролл это число и нет происходит подгрузка сообщений, то запоминаем его последнюю позицию
				return (): void => {
					if (typeof scrollStore.scrollPosition === 'number' && !loadingStore.showLoading) {
						scrollStore.setLastScrollPosition(scrollStore.scrollPosition);
					}
				};
			}, [loadingStore.showLoading, scrollStore]);

			//создаём налюдателя. Скролит вниз в мобильной версии, когда пользователь меняет размер чата (включает клавиатуру)
			useLayoutEffect(() => {
				const target = scrollBarWrapperRef.current;
				if (target && isMobile) {
					const observer = new ResizeObserver(() => {
						scrollStore.scrollToBottom();
					});
					observer.observe(target);
					return (): void => {
						observer.unobserve(target);
					};
				}
			}, [scrollStore]);

			return (
				<div className={className} ref={scrollBarWrapperRef}>
					<DateDelimiterSticky />
					<Scrollbars
						renderThumbVertical={CustomRenderThumbVertical}
						ref={scrollbarRef}
						renderView={CustomRenderThumbView}
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						//@ts-ignore
						onScroll={onScroll}
					>
						{children}
					</Scrollbars>
					<ScrollToBottomButton />
				</div>
			);
		}
	)
)(css`
	display: flex;
	flex-flow: column;
	height: 100%;
	overflow: visible;
	position: relative;
	& > div {
		flex-grow: 1;
	}
`);
